/**
 * Billing review > Exports: every batch with its file, and the billing
 * systems CaseBlink will push to later. A batch locks its entries until it
 * is voided; voided batches stay listed for audit.
 */
import {
  Download,
  FileSpreadsheet,
  FileText,
  Lock,
  MoreHorizontal,
  Plus,
  Undo2
} from "lucide-react";
import { useState, type FC } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import { exportActions } from "../actions";
import { useExports, useProviders } from "../queries";
import type { ExportBatch } from "../types";
import { formatHours, formatMoney } from "../utils";
import {
  batchPeriod,
  createdAt,
  extensionFor,
  initials,
  plural
} from "./exportModel";

const BatchMenu: FC<{ batch: ExportBatch; onVoid: () => void }> = ({
  batch,
  onVoid
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        variant="ghost"
        size="icon"
        className="size-7"
        aria-label={`More for ${batch.ref}`}
      >
        <MoreHorizontal />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-56">
      <DropdownMenuItem onSelect={() => void exportActions.download(batch)}>
        <Download /> Download {extensionFor(batch.format)}
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        variant="destructive"
        disabled={batch.voided}
        onSelect={onVoid}
      >
        <Undo2 /> {batch.voided ? "Already voided" : "Void and unlock entries"}
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const BatchesTable: FC<{
  batches: ExportBatch[];
  onVoid: (b: ExportBatch) => void;
}> = ({ batches, onVoid }) => (
  <div className="shrink-0 overflow-hidden rounded-xl border border-line-2">
    <Table className="text-base">
      <TableHeader className="bg-fill-1">
        <TableRow>
          <TableHead className="h-8 w-24 text-sm">Batch</TableHead>
          <TableHead className="h-8 w-32 text-sm">Format</TableHead>
          <TableHead className="h-8 text-sm">Period</TableHead>
          <TableHead className="h-8 w-20 text-right text-sm">Entries</TableHead>
          <TableHead className="h-8 w-20 text-right text-sm">Hours</TableHead>
          <TableHead className="h-8 w-28 text-right text-sm">Amount</TableHead>
          <TableHead className="h-8 w-40 text-sm">Created</TableHead>
          <TableHead className="h-8 w-24 text-sm">Status</TableHead>
          <TableHead className="h-8 w-10" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {batches.map((b) => (
          <TableRow
            key={b.id}
            className={cn(b.voided && "opacity-60")}
            data-testid={`batch-${b.ref}`}
          >
            <TableCell className="font-mono text-sm">{b.ref}</TableCell>
            <TableCell>
              <span className="inline-flex items-center gap-1.5">
                {b.format.startsWith("ledes") ? (
                  <FileText className="size-3.5 text-fg-3" />
                ) : (
                  <FileSpreadsheet className="size-3.5 text-fg-3" />
                )}
                {b.format_label}
              </span>
            </TableCell>
            <TableCell className="text-fg-3">{batchPeriod(b)}</TableCell>
            <TableCell className="text-right tabular-nums">
              <span className="flex flex-col leading-4">
                <span>{b.entry_count}</span>
                {b.expense_count ? (
                  <span className="text-sm text-fg-3">
                    +{plural(b.expense_count, "expense", "expenses")}
                  </span>
                ) : null}
              </span>
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatHours(b.total_hours)}
            </TableCell>
            <TableCell
              className={cn(
                "text-right tabular-nums",
                b.voided && "line-through"
              )}
            >
              {formatMoney(b.total_amount)}
            </TableCell>
            <TableCell>
              <span className="flex flex-col leading-4">
                <span className="text-sm">{b.created_by_name || "Admin"}</span>
                <span className="text-sm text-fg-3">
                  {createdAt(b.created_at)}
                </span>
              </span>
            </TableCell>
            <TableCell>
              {b.voided ? (
                <Badge variant="secondary">Voided</Badge>
              ) : (
                <Badge
                  variant="secondary"
                  className="border-success-line bg-success-soft text-success"
                >
                  <Lock className="size-2.5" /> Locked
                </Badge>
              )}
            </TableCell>
            <TableCell>
              <BatchMenu batch={b} onVoid={() => onVoid(b)} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

export const ExportsTab: FC<{ onExport: () => void }> = ({ onExport }) => {
  const batches = useExports();
  const providers = useProviders().data ?? [];
  const [voiding, setVoiding] = useState<ExportBatch | null>(null);
  const [confirming, setConfirming] = useState(false);
  const askToVoid = (batch: ExportBatch) => {
    setVoiding(batch);
    setConfirming(true);
  };
  const rows = batches.data ?? [];

  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4"
      data-testid="exports-tab"
    >
      <div className="flex flex-col gap-2">
        <span className="flex items-center">
          <span className="text-base font-medium">Export batches</span>
          <span className="flex-1" />
          <Button variant="secondary" size="xs" onClick={onExport}>
            <Plus /> New export
          </Button>
        </span>
        {batches.isLoading ? (
          <Skeleton className="h-24" />
        ) : rows.length ? (
          <BatchesTable batches={rows} onVoid={askToVoid} />
        ) : (
          <div className="rounded-xl border border-dashed border-line-2 p-6 text-center text-base text-fg-3">
            No exports yet. Approved time is ready when you are.
          </div>
        )}
        <span className="text-sm text-fg-3">
          Exporting locks entries so the file always matches CaseBlink. Voiding
          a batch unlocks them; the voided file stays here for audit.
        </span>
      </div>

      <div className="flex flex-col gap-2.5">
        <span className="text-base font-medium">Billing systems</span>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {providers.map((p) => (
            <div
              key={p.id}
              className="flex flex-col gap-2 rounded-xl border border-line-2 p-3.5"
            >
              <span className="flex items-center gap-2">
                <span className="inline-flex size-7 items-center justify-center rounded-md border border-line-2 bg-fill-2 text-sm font-medium text-fg-3">
                  {initials(p.name)}
                </span>
                <span className="text-base font-medium">{p.name}</span>
                <span className="flex-1" />
                <Badge variant="secondary">
                  {p.status === "available"
                    ? p.connected
                      ? "Connected"
                      : "Available"
                    : "Coming Soon"}
                </Badge>
              </span>
              <span className="text-sm leading-4.5">{p.what}</span>
              <span className="text-sm leading-4.25 text-fg-3">{p.how}</span>
            </div>
          ))}
        </div>
        <span className="text-sm text-fg-3">
          Until then, LEDES 1998B imports into most legal billing and e-billing
          systems, and the CSV uses fixed, documented columns.
        </span>
      </div>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Void {voiding?.ref}?</AlertDialogTitle>
            <AlertDialogDescription>
              {voiding
                ? `Voiding unlocks ${plural(
                    voiding.entry_count,
                    "entry",
                    "entries"
                  )}${
                    voiding.expense_count
                      ? ` and ${plural(
                          voiding.expense_count,
                          "expense",
                          "expenses"
                        )}`
                      : ""
                  } so ${
                    voiding.entry_count + (voiding.expense_count ?? 0) === 1
                      ? "it"
                      : "they"
                  } can be edited and exported again. If you already imported the file, reverse it in your billing system.`
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (voiding) void exportActions.void(voiding);
                setConfirming(false);
              }}
            >
              Void and unlock
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
